import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Loader2, ChevronsUpDown } from 'lucide-react'
import { showSuccess, showApiError, showWarning } from '@/lib/utils/notifications'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { ChangeReasonModal } from '@features/document-management/components/ChangeReasonModal'
import applicationStyles from '@/features/application/styles/applicationList.module.css'
import { getEmailTemplates, updateEmailTemplateStatus } from '../services/emailTemplatesService'
import { EMAIL_TEMPLATES_ROUTES } from '../constants/routes'
import type {
  EmailTemplate,
  EmailTemplateListParams,
  EmailTemplateSortKey,
  EmailTemplateStatus,
} from '../types/emailTemplate'
import { EMAIL_TEMPLATE_PER_PAGE_OPTIONS, PRIMARY_BRAND } from '../utils/constants'
import { formatWorkflowLabel } from '../utils/formatWorkflowLabel'
import { cn } from '@/lib/utils/helpers'

const DEFAULT_PARAMS: EmailTemplateListParams = {
  page: 1,
  perPage: 15,
  sortBy: 'asc',
  orderBy: 'template_name',
}

function PaginationControls({
  className,
  currentPage,
  totalPages,
  onPage,
}: {
  className?: string
  currentPage: number
  totalPages: number
  onPage: (n: number) => void
}) {
  if (totalPages < 1) return null
  return (
    <div className={className}>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          className="min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          onClick={() => onPage(1)}
          disabled={currentPage === 1}
          title="First"
        >
          &laquo;
        </button>
        <button
          type="button"
          className="min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          onClick={() => onPage(currentPage - 1)}
          disabled={currentPage === 1}
          title="Previous"
        >
          &lsaquo;
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum: number
          if (totalPages <= 5) {
            pageNum = i + 1
          } else if (currentPage <= 3) {
            pageNum = i + 1
          } else if (currentPage >= totalPages - 2) {
            pageNum = totalPages - 4 + i
          } else {
            pageNum = currentPage - 2 + i
          }
          const isActive = currentPage === pageNum
          return (
            <button
              key={pageNum}
              type="button"
              className={cn(
                'min-w-[32px] h-8 flex items-center justify-center rounded-lg border text-sm font-medium transition-all px-2',
                isActive
                  ? 'border border-button bg-button text-white shadow-md shadow-slate-300/30'
                  : 'border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
              )}
              onClick={() => onPage(pageNum)}
            >
              {pageNum}
            </button>
          )
        })}
        <button
          type="button"
          className="min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          onClick={() => onPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          title="Next"
        >
          &rsaquo;
        </button>
        <button
          type="button"
          className="min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          onClick={() => onPage(totalPages)}
          disabled={currentPage === totalPages}
          title="Last"
        >
          &raquo;
        </button>
      </div>
    </div>
  )
}

const SESSION_KEY = 'emailtemplates_filter'

function loadSessionParams(): EmailTemplateListParams {
  try {
    const saved = sessionStorage.getItem(SESSION_KEY)
    if (saved) {
      const parsed = JSON.parse(saved) as EmailTemplateListParams
      if (parsed && typeof parsed === 'object') {
        return {
          ...DEFAULT_PARAMS,
          ...parsed,
        }
      }
    }
  } catch {
    showWarning('Could not restore your saved email template filters.')
  }
  return DEFAULT_PARAMS
}

function saveSessionParams(params: EmailTemplateListParams) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(params))
  } catch {
    showWarning('Could not save your email template filter preferences.')
  }
}

export function EmailTemplatesListPage() {
  const navigate = useNavigate()
  const initialParams = loadSessionParams()

  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [params, setParams] = useState<EmailTemplateListParams>(initialParams)
  const [pagination, setPagination] = useState({ current: 1, last: 1, total: 0 })
  const [searchInput, setSearchInput] = useState(initialParams.search ?? '')
  const [statusInput, setStatusInput] = useState<'Active' | 'Disabled' | ''>(initialParams.status ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmToggle, setConfirmToggle] = useState<EmailTemplate | null>(null)
  /** Row pending status change after confirm; separate from `confirmToggle` so the confirm can close before the reason modal (avoids two stacked overlays / z-index freeze). */
  const [statusChangeTarget, setStatusChangeTarget] = useState<EmailTemplate | null>(null)
  const [reasonOpen, setReasonOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const mountedRef = useRef(false)

  const load = useCallback(async (next: EmailTemplateListParams) => {
    setLoading(true)
    setError(null)
    try {
      const response = await getEmailTemplates(next)
      setTemplates(response.data ?? [])
      setPagination({
        current: response.current ?? 1,
        last: response.last ?? 1,
        total: response.total ?? response.data?.length ?? 0,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load email templates')
      showApiError(e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load(initialParams)
  }, [load])

  /** Search filters the list while typing (debounced); status still updates immediately via `applyStatusFilter`. */
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nextSearch = searchInput.trim() || undefined
      setParams((prev) => {
        if ((prev.search ?? undefined) === nextSearch) return prev
        const next = { ...prev, search: nextSearch, page: 1 }
        saveSessionParams(next)
        void load(next)
        return next
      })
    }, 400)
    return () => window.clearTimeout(timer)
  }, [searchInput, load])

  const applyParams = useCallback(
    (next: EmailTemplateListParams) => {
      setParams(next)
      saveSessionParams(next)
      void load(next)
    },
    [load],
  )

  const changePerPage = (n: number) => {
    applyParams({ ...params, perPage: n, page: 1 })
  }

  const changePage = (n: number) => applyParams({ ...params, page: n })

  const toggleSort = (key: EmailTemplateSortKey) => {
    const nextDir: 'asc' | 'desc' = params.orderBy === key && params.sortBy === 'asc' ? 'desc' : 'asc'
    applyParams({ ...params, orderBy: key, sortBy: nextDir, page: 1 })
  }

  const submitFilter = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    applyParams({
      ...params,
      search: searchInput.trim() || undefined,
      status: statusInput || undefined,
      page: 1,
    })
  }

  const applyStatusFilter = (next: 'Active' | 'Disabled' | '') => {
    setStatusInput(next)
    const nextStatus = next || undefined
    const nextSearch = searchInput.trim() || undefined
    setParams((prev) => {
      if (
        (prev.status ?? undefined) === nextStatus &&
        (prev.search ?? undefined) === nextSearch
      ) {
        return prev
      }
      const merged = { ...prev, search: nextSearch, status: nextStatus, page: 1 }
      saveSessionParams(merged)
      void load(merged)
      return merged
    })
  }

  const resetFilter = () => {
    setSearchInput('')
    setStatusInput('')
    try {
      sessionStorage.removeItem(SESSION_KEY)
    } catch {
      // ignore
    }
    applyParams({
      page: 1,
      perPage: 15,
      sortBy: 'asc',
      orderBy: 'template_name',
    })
  }

  const openEdit = (template: EmailTemplate) => {
    navigate(EMAIL_TEMPLATES_ROUTES.edit(template.template_id))
  }

  const handleReasonSubmit = async (reason: string) => {
    const template = statusChangeTarget
    if (!template) return
    const nextStatus: EmailTemplateStatus =
      template.template_status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE'
    setSubmitting(true)
    try {
      await updateEmailTemplateStatus(template.template_id, {
        template_status: nextStatus,
        fk_workflow_id: template.fk_workflow_id,
        change_reason: reason,
      })
      showSuccess('Status updated successfully!')
      setReasonOpen(false)
      setStatusChangeTarget(null)
      setConfirmToggle(null)
      void load(params)
    } catch (e) {
      showApiError(e)
    } finally {
      setSubmitting(false)
    }
  }

  const acceptToggle = () => {
    if (!confirmToggle) return
    // Close confirm first — same tick as opening reason is OK; never stack two body-locked modals
    // with the high-z confirm on top of the reason dialog (that froze the UI).
    setStatusChangeTarget(confirmToggle)
    setConfirmToggle(null)
    setReasonOpen(true)
  }

  const currentPage = pagination.current || 1
  const totalPages = Math.max(1, pagination.last || 1)
  const perPage = params.perPage || 15

  const SortIndicator = ({ column }: { column: EmailTemplateSortKey }) => {
    const isActive = params.orderBy === column
    return (
      <span
        className={`${applicationStyles.sortIcon} ${isActive ? applicationStyles.sortIconActive : ''}`}
        aria-hidden
      >
        {isActive ? (params.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3" />}
      </span>
    )
  }

  return (
    <div
      className="min-h-screen min-w-0 bg-[#f8fafc] font-sans text-slate-900 pb-12"
      data-testid="email-templates-list-page"
    >
      <PageHeader title="EMAIL TEMPLATES" testIdRoot="email-templates-page-header" />

      <div
        className="mx-auto flex w-full min-w-0 flex-col gap-4 px-3 py-4 sm:gap-6 sm:px-6 sm:py-8"
        data-testid="email-templates-list-main"
      >
        <div className="flex w-full min-w-0 flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <form
            onSubmit={submitFilter}
            className="flex w-full min-w-0 flex-1 flex-wrap items-end gap-2 md:max-w-3xl"
            data-testid="email-templates-search-form"
          >
            <div className="relative w-full min-w-0 sm:flex-1 sm:max-w-md">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
              </div>
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search templates..."
                className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15 focus:border-[rgb(21_151_255)] transition-all placeholder:text-slate-400"
                data-testid="email-templates-search"
              />
            </div>
            <select
              value={statusInput}
              onChange={(e) =>
                applyStatusFilter(e.target.value as 'Active' | 'Disabled' | '')
              }
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-[rgb(21_151_255)] focus:ring-2 focus:ring-[rgb(21_151_255)]/15"
              data-testid="email-templates-status-filter"
            >
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Disabled">Disabled</option>
            </select>
            <button
              type="submit"
              className="rounded-xl bg-[rgb(21_151_255)] px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:opacity-95"
              title="Apply filters immediately"
            >
              Search
            </button>
            <button
              type="button"
              onClick={resetFilter}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Reset
            </button>
          </form>
          <button
            type="button"
            onClick={() => navigate(EMAIL_TEMPLATES_ROUTES.add)}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-[rgb(21_151_255)] px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[rgba(21,151,255,0.25)] transition hover:-translate-y-0.5"
            data-testid="email-templates-add-button"
          >
            <Plus className="h-5 w-5" />
            Add template
          </button>
        </div>

        <div
          className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
          data-testid="email-templates-results-card"
        >
          {loading && (
            <div className="flex justify-center items-center py-20" data-testid="email-templates-loading">
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: PRIMARY_BRAND }} />
            </div>
          )}

          {error && !loading && (
            <div className="p-4 sm:p-6 text-center text-sm text-red-600 bg-red-50 m-3 rounded-lg">{error}</div>
          )}

          {!loading && !error && (
            <>
              <div className="flex flex-col items-stretch justify-between gap-3 border-b border-slate-100 bg-slate-50/50 px-3 py-3 sm:flex-row sm:items-center sm:px-6 sm:py-4">
                <div className="text-left text-xs text-slate-500 sm:text-sm">
                  Page <span className="font-bold text-slate-900">{currentPage}</span> of{' '}
                  <span className="font-bold text-slate-900">{totalPages}</span>
                  <span className="mx-2 text-slate-300">|</span>
                  Total <span className="font-bold text-slate-900">{pagination.total}</span> records
                </div>
                <div className="flex flex-wrap items-center justify-center sm:justify-end">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPage={changePage}
                  />
                </div>
              </div>

              <div className="overflow-x-auto" data-testid="email-templates-table-wrap">
                <table
                  className={applicationStyles.listingTable}
                  data-testid="email-templates-table"
                >
                  <thead>
                    <tr>
                      <th
                        className="cursor-pointer"
                        onClick={() => toggleSort('template_id')}
                      >
                        <button type="button" className={applicationStyles.sortHeaderButton} onClick={(e) => { e.stopPropagation(); toggleSort('template_id'); }}>
                          <span className={applicationStyles.sortHeaderLabel}>ID</span>
                          <SortIndicator column="template_id" />
                        </button>
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => toggleSort('template_name')}
                      >
                        <button type="button" className={applicationStyles.sortHeaderButton} onClick={(e) => { e.stopPropagation(); toggleSort('template_name'); }}>
                          <span className={applicationStyles.sortHeaderLabel}>Template name</span>
                          <SortIndicator column="template_name" />
                        </button>
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => toggleSort('template_description')}
                      >
                        <button type="button" className={applicationStyles.sortHeaderButton} onClick={(e) => { e.stopPropagation(); toggleSort('template_description'); }}>
                          <span className={applicationStyles.sortHeaderLabel}>Description</span>
                          <SortIndicator column="template_description" />
                        </button>
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => toggleSort('workflow_type')}
                      >
                        <button type="button" className={applicationStyles.sortHeaderButton} onClick={(e) => { e.stopPropagation(); toggleSort('workflow_type'); }}>
                          <span className={applicationStyles.sortHeaderLabel}>Workflow</span>
                          <SortIndicator column="workflow_type" />
                        </button>
                      </th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                          No email templates found.
                        </td>
                      </tr>
                    ) : (
                      templates.map((template) => (
                        <tr key={template.template_id}>
                          <td className="font-mono">
                            {template.template_id}
                          </td>
                          <td className="font-medium text-slate-900">
                            {template.template_name}
                          </td>
                          <td className="max-w-[280px] sm:max-w-md">
                            {(template.template_description || '').length > 120
                              ? `${(template.template_description || '').slice(0, 120)}…`
                              : template.template_description || '—'}
                          </td>
                          <td className="whitespace-nowrap">
                            {formatWorkflowLabel(template.workflow_type) ||
                              formatWorkflowLabel(template.workflow_name) ||
                              '—'}
                          </td>
                          <td className="text-right">
                            <div className="inline-flex flex-wrap items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => setConfirmToggle(template)}
                                className={cn(
                                  'rounded-full border px-3 py-1.5 text-xs font-semibold shadow-sm transition-colors',
                                  template.template_status === 'ACTIVE'
                                    ? 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                                    : 'border-[rgb(21_151_255)] bg-[rgb(21_151_255)] text-white hover:opacity-95',
                                )}
                                data-testid={`email-template-status-btn-${template.template_id}`}
                              >
                                {template.template_status === 'ACTIVE' ? 'Disable' : 'Active'}
                              </button>
                              <button
                                type="button"
                                onClick={() => openEdit(template)}
                                className="p-1.5 rounded-lg hover:bg-[rgb(21_151_255)]/10 text-[rgb(21_151_255)]"
                                title="Edit"
                                aria-label="Edit"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col items-stretch justify-between gap-3 border-t border-slate-100 bg-slate-50/50 px-3 py-3 sm:flex-row sm:items-center sm:px-6 sm:py-4">
                <div className="text-left text-xs text-slate-500 sm:text-sm">
                  Page <span className="font-bold text-slate-900">{currentPage}</span> of{' '}
                  <span className="font-bold text-slate-900">{totalPages}</span>
                  <span className="mx-2 text-slate-300">|</span>
                  Total <span className="font-bold text-slate-900">{pagination.total}</span> records
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPage={changePage}
                  />
                  <div className="flex items-center gap-2 pl-0 sm:pl-4">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Rows</span>
                    <select
                      className="block rounded-lg border border-slate-200 bg-white p-2 text-sm text-slate-700 focus:border-button focus:outline-none focus:ring-2 focus:ring-button/30"
                      value={perPage}
                      onChange={(e) => changePerPage(parseInt(e.target.value, 10))}
                      data-testid="email-templates-per-page"
                    >
                      {EMAIL_TEMPLATE_PER_PAGE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmToggle !== null}
        title="Template status"
        message={
          confirmToggle
            ? `Update template status to ${
                confirmToggle.template_status === 'ACTIVE' ? 'Disable' : 'Active'
              }?`
            : ''
        }
        primaryLabel="Confirm"
        onPrimary={acceptToggle}
        secondaryLabel="Cancel"
        onSecondary={() => setConfirmToggle(null)}
      />

      <ChangeReasonModal
        isOpen={reasonOpen}
        submitting={submitting}
        onClose={() => {
          setReasonOpen(false)
          setStatusChangeTarget(null)
          setConfirmToggle(null)
        }}
        onSubmit={handleReasonSubmit}
      />
    </div>
  )
}
